/**
 * Domain Layer - ProcessingResult ValueObject
 * 診療予約データ加工結果の値オブジェクト定義
 */

import type { ProcessedReservation } from './MedicalReservation';

/**
 * ステータス変換ログエントリ
 */
export interface StatusConversionLogEntry {
  /** 行インデックス */
  readonly rowIndex: number;
  /** 患者名またはID */
  readonly patientIdentifier: string;
  /** 変換前のステータス */
  readonly originalStatus: string;
  /** 変換後のステータス */
  readonly convertedStatus: string;
  /** 変換理由 */
  readonly reason?: string;
}

/**
 * ステータス変換ログ
 */
export interface StatusConversionLog {
  /** 変換エントリの配列 */
  readonly entries: readonly StatusConversionLogEntry[];
  /** 総変換件数 */
  readonly totalConversions: number;
}

/**
 * 医師割り当てログエントリ
 */
export interface DoctorAssignmentLogEntry {
  /** 行インデックス */
  readonly rowIndex: number;
  /** 患者名またはID */
  readonly patientIdentifier: string;
  /** 割り当て前の医師名 */
  readonly originalDoctor: string;
  /** 割り当て後の医師名 */
  readonly assignedDoctor: string;
  /** 割り当て理由 */
  readonly reason?: string;
}

/**
 * 医師割り当てログ
 */
export interface DoctorAssignmentLog {
  /** 割り当てエントリの配列 */
  readonly entries: readonly DoctorAssignmentLogEntry[];
  /** 総割り当て件数 */
  readonly totalAssignments: number;
}

/**
 * 処理サマリー
 */
export interface ProcessingSummary {
  /** 総データ件数 */
  readonly totalCount: number;
  /** 正常処理件数 */
  readonly successCount: number;
  /** スキップ件数 */
  readonly skippedCount: number;
  /** エラー件数 */
  readonly errorCount: number;
}

/**
 * データ加工結果を表す値オブジェクト
 */
export interface ProcessingResult {
  /** 加工済みデータ */
  readonly processedData: readonly ProcessedReservation[];
  /** ステータス変換ログ */
  readonly conversionLog: StatusConversionLog;
  /** 医師割り当てログ */
  readonly assignmentLog: DoctorAssignmentLog;
  /** 処理サマリー */
  readonly summary: ProcessingSummary;
}

/**
 * 空のProcessingResultを生成する
 * @returns 空のProcessingResult
 */
export const createEmptyProcessingResult = (): ProcessingResult => ({
  processedData: [],
  conversionLog: {
    entries: [],
    totalConversions: 0,
  },
  assignmentLog: {
    entries: [],
    totalAssignments: 0,
  },
  summary: {
    totalCount: 0,
    successCount: 0,
    skippedCount: 0,
    errorCount: 0,
  },
});

/**
 * ProcessingResultを生成する
 * @param processedData 加工済みデータ
 * @param conversionEntries ステータス変換ログエントリ
 * @param assignmentEntries 医師割り当てログエントリ
 * @param summary 処理サマリー
 * @returns ProcessingResult
 */
export const createProcessingResult = (
  processedData: ProcessedReservation[],
  conversionEntries: StatusConversionLogEntry[],
  assignmentEntries: DoctorAssignmentLogEntry[],
  summary: ProcessingSummary
): ProcessingResult => ({
  processedData: Object.freeze([...processedData]),
  conversionLog: {
    entries: Object.freeze([...conversionEntries]),
    totalConversions: conversionEntries.length,
  },
  assignmentLog: {
    entries: Object.freeze([...assignmentEntries]),
    totalAssignments: assignmentEntries.length,
  },
  summary,
});

/**
 * ProcessingResultが成功かどうかを判定する
 * @param result ProcessingResult
 * @returns エラーがない場合true
 */
export const isProcessingSuccessful = (result: ProcessingResult): boolean => {
  return result.summary.errorCount === 0;
};
